import * as React from 'react'
import { FormItem, FormItemName, ValidationMessage } from 'uiComponents/form/formElements'
import { TextInput } from 'uiComponents/input'
import { Section, SectionForm, InputRow, InputColumn, PageActions } from 'uiComponents/settingsContainer'
import { ValidationCheckmark } from 'uiComponents/validationCheckmark'
import { Profile } from 'settings/schema'
import { AccountSettingsService } from 'settings/settingsService'
import { ActionButton } from 'uiComponents/buttons'
import { delay } from 'utils'
import { ChartDataLoader } from 'uiComponents/loaders'
import { showSuccessMessage, showErrorMessage, hideAllMessages } from 'uiComponents/message'
import { t } from 'translations'

interface PasswordSectionProps {
    profile: Profile
    accountSettingsService: AccountSettingsService
    setActiveSection: (section: string) => void
}

interface PasswordSectionState {
    oldPassword: string
    newPassword: string
    newPasswordRepeat: string
    oldPassMsg: string
    newPassRepeatMsg: string
    passValid: boolean | null
    loading: boolean
}

class PasswordSection extends React.Component<PasswordSectionProps, PasswordSectionState> {
    constructor(props: PasswordSectionProps) {
        super(props)
        this.state = {
            oldPassword: '',
            newPassword: '',
            newPasswordRepeat: '',
            oldPassMsg: '',
            newPassRepeatMsg: '',
            passValid: null,
            loading: false,
        }
    }

    componentDidMount() {
        this.props.setActiveSection('password')
    }

    onSave = async () => {
        hideAllMessages()
        if (!(await this.checkFormValid())) {
            return
        }
        this.setState({ loading: true })
        try {
            const resp = await this.props.accountSettingsService.updatePassword(
                this.state.oldPassword,
                this.state.newPassword,
            )
            if (resp.data && resp.data.changePassword.ok) {
                this.setState({
                    oldPassword: '',
                    newPassword: '',
                    newPasswordRepeat: '',
                    loading: false,
                })
                showSuccessMessage(t('settings.personalSettings.password.updated'))
                await delay(5000)
                hideAllMessages()
            } else if (resp.data && resp.data.changePassword.errCode) {
                this.setErrorMesssage(resp.data.changePassword.errCode)
            } else {
                this.setErrorMesssage('unknown')
            }
        } catch (_) {
            this.setErrorMesssage('unknown')
        }
    }

    setErrorMesssage = (errorMessage: string) => {
        this.setState({ loading: false })
        if (errorMessage === 'passInvalid') {
            this.setState({
                oldPassMsg: t('settings.personalSettings.password.currentPasswordWrong'),
            })
        } else {
            showErrorMessage(t('settings.personalSettings.messages.genericError'))
        }
    }

    checkFormValid = async () => {
        const oldPassProvided = this.checkOldPassProvided()
        const passValid = this.checkValidPass()
        const passMatch = this.checkPassMatch()
        if (!oldPassProvided || !passValid || !passMatch) {
            return false
        }
        return true
    }

    checkOldPassProvided = () => {
        if (!this.state.oldPassword) {
            this.setState({ oldPassMsg: t('settings.personalSettings.password.currentPasswordRequired') })
            return false
        } else {
            this.setState({ oldPassMsg: '' })
        }
        return true
    }

    checkValidPass = () => {
        if (this.state.newPassword.length < 12) {
            this.setState({ passValid: false })
            return false
        } else {
            this.setState({ passValid: true })
        }
        if (this.state.newPasswordRepeat) {
            this.checkPassMatch()
        }
        return true
    }

    checkPassMatch = () => {
        if (this.state.newPassword !== this.state.newPasswordRepeat) {
            this.setState({ newPassRepeatMsg: t('settings.personalSettings.passwordsDoNotMatch') })
            return false
        } else {
            this.setState({ newPassRepeatMsg: '' })
        }
        return true
    }

    handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ [event.target.name]: event.target.value } as any)
    }

    render() {
        const { oldPassword, newPassword, newPasswordRepeat, passValid, oldPassMsg, newPassRepeatMsg } = this.state
        return (
            <Section>
                {this.state.loading && <ChartDataLoader />}
                <SectionForm noValidate style={{ minHeight: '23em' }}>
                    <div>
                        <InputRow>
                            <InputColumn>
                                <FormItem htmlFor="oldPassword">
                                    <FormItemName>
                                        {t('settings.personalSettings.password.currentPassword')}
                                    </FormItemName>
                                    <TextInput
                                        id="oldPassword"
                                        name="oldPassword"
                                        type="password"
                                        value={oldPassword ? oldPassword : ''}
                                        onFinishTyping={this.checkOldPassProvided}
                                        onChange={this.handleInputChange}
                                        status={oldPassMsg ? 'error' : 'normal'}
                                        block={true}
                                    />
                                </FormItem>
                                <ValidationMessage className={oldPassMsg ? 'validation-message-visible' : ''}>
                                    {oldPassMsg || '&nbsp;'}
                                </ValidationMessage>
                            </InputColumn>
                            <InputColumn />
                        </InputRow>
                        <InputRow>
                            <InputColumn>
                                <FormItem htmlFor="newPassword">
                                    <FormItemName>{t('settings.personalSettings.password.newPassword')}</FormItemName>
                                    <TextInput
                                        id="newPassword"
                                        name="newPassword"
                                        type="password"
                                        value={newPassword ? newPassword : ''}
                                        onChange={this.handleInputChange}
                                        onFinishTyping={this.checkValidPass}
                                        status={passValid || passValid == null ? 'normal' : 'error'}
                                        block={true}
                                    />
                                </FormItem>
                                <ValidationCheckmark
                                    text={t('settings.personalSettings.passwordMinLength')}
                                    valid={passValid}
                                />
                            </InputColumn>
                            <InputColumn>
                                <FormItem htmlFor="newPasswordRepeat">
                                    <FormItemName>
                                        {t('settings.personalSettings.password.repeatNewPassword')}
                                    </FormItemName>
                                    <TextInput
                                        id="newPasswordRepeat"
                                        name="newPasswordRepeat"
                                        type="password"
                                        value={newPasswordRepeat ? newPasswordRepeat : ''}
                                        onChange={this.handleInputChange}
                                        onFinishTyping={this.checkPassMatch}
                                        status={newPassRepeatMsg ? 'error' : 'normal'}
                                        block={true}
                                    />
                                </FormItem>
                                <ValidationMessage className={newPassRepeatMsg ? 'validation-message-visible' : ''}>
                                    {newPassRepeatMsg || '&nbsp;'}
                                </ValidationMessage>
                            </InputColumn>
                        </InputRow>
                    </div>
                    <PageActions>
                        <ActionButton onClick={this.onSave}>{t('common.save')}</ActionButton>
                    </PageActions>
                </SectionForm>
            </Section>
        )
    }
}

export default PasswordSection
